import { PointerEvent, use, useCallback } from 'react';
import styled from 'styled-components';
import PauseButtonIcon from '../assets/pause-circle.svg?react';
import PlayButtonIcon from '../assets/play-circle.svg?react';
import { CurrentSongContext } from './current_song.tsx';
import { FadingButton, useButtonFade } from './player_util.tsx';
import { SequencerContext } from './sequencer.tsx';

export interface Props {
  delayPlayback?: boolean;
  onClick?: (playing: boolean) => void;
  abovePdf?: boolean;
  noShadow?: boolean;
}

export function PlayButton({ delayPlayback, onClick, abovePdf, noShadow }: Props) {
  const { playing, sequencer } = use(SequencerContext);
  const fade = useButtonFade();
  const { tutorialPlayingSong } = use(CurrentSongContext);

  const onClickCapture = useCallback(
    (event: any) => {
      if (tutorialPlayingSong) {
        return;
      }
      if (playing) {
        sequencer.pause();
      } else {
        if (delayPlayback) {
          setTimeout(() => sequencer.play(), 300);
        } else {
          sequencer.play();
        }
      }
      onClick?.(playing);
      event.stopPropagation(); // prevent mixer from closing
    },
    [delayPlayback, onClick, playing, sequencer, tutorialPlayingSong],
  );

  const onMouseDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault(); // avoid stealing focus from search input
  }, []);

  return (
    <PlayButtonWrapper
      id={playButtonId}
      onClickCapture={onClickCapture}
      $abovePdf={abovePdf}
      $fade={fade}
      $noShadow={noShadow}
      onMouseDown={onMouseDown}
    >
      {playing ? <PauseButtonIconWrapper /> : <PlayButtonIconWrapper />}
    </PlayButtonWrapper>
  );
}

export const playButtonId = 'play-button';

const PlayButtonWrapper = styled(FadingButton)<{ $abovePdf?: boolean; $fade?: boolean; $noShadow?: boolean }>`
  padding: 0 0 0 0;
  cursor: pointer;
  width: 100%;
  height: 100%;

  &:active {
    ${(props) => (props.$noShadow ? '' : 'transform: scale(110%);')}
  }

  border-radius: 50%;
  /* the svg path is the disc ('fill') with a cut-out glyph showing the 'background' through it;
     while faded over the sheet music, drop the purple for the pdf colors so the button reads as
     transparent gray (issue #3389); hover un-fades (see FadingButton), so it restores the colors */
  --button-background: var(${(props) => (props.$abovePdf ? '--color-play-button-icon' : '--color-background')});
  --button-fill: var(${(props) => (props.$abovePdf ? '--color-play-button' : '--color-text')});
  background: ${(props) => (props.$abovePdf && props.$fade ? 'var(--color-pdf-background)' : 'var(--button-background)')};
  fill: ${(props) => (props.$abovePdf && props.$fade ? 'var(--color-pdf-text)' : 'var(--button-fill)')};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: var(--button-background);
      fill: var(--button-fill);
    }
  }
`;

const PlayButtonIconWrapper = styled(PlayButtonIcon)``;

const PauseButtonIconWrapper = styled(PauseButtonIcon)``;
